import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { AxiosError } from 'axios'
import {
  getRefreshCredentials,
  setWorkspaceAuthSession,
  type WorkspaceAuthSession,
} from '@amglobal/auth-shared'
import {
  AUTH_OMIT_BEARER_RELATIVE_PATHS,
  normalizeAuthRelativePath,
  postRefreshToken,
  registerAuthRouteRequestInterceptor,
} from '@/features/auth/api'
import {
  authResponseToSession,
  clearWorkspaceSession,
} from '@/features/auth/session/workspace-session'
import { forceSessionExpired } from '@/shared/auth/session-expired'

const RETRY_FLAG = '__authRetry'

let refreshInFlight: Promise<boolean> | null = null

/** 401 on these paths must not trigger another refresh (avoids deadlock with refresh-token). */
export function shouldSkipAuthRefreshForUrl(url: string | undefined): boolean {
  return AUTH_OMIT_BEARER_RELATIVE_PATHS.has(normalizeAuthRelativePath(url))
}

async function coordinatedRefresh(client: AxiosInstance): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = executeRefresh(client).finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/** Attempt token refresh; exported for proactive refresh before expiry. */
export async function tryRefreshSession(client: AxiosInstance): Promise<boolean> {
  return coordinatedRefresh(client)
}

async function executeRefresh(client: AxiosInstance): Promise<boolean> {
  const creds = getRefreshCredentials()
  if (!creds) {
    clearWorkspaceSession()
    return false
  }
  try {
    const response = await postRefreshToken(client, creds)
    const session: WorkspaceAuthSession = authResponseToSession(response)
    setWorkspaceAuthSession(session)
    return true
  } catch {
    clearWorkspaceSession()
    return false
  }
}

function handleUnrecoverableAuthFailure(): void {
  forceSessionExpired()
}

function markRetried(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const extra = config as InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean }
  extra[RETRY_FLAG] = true
  return config
}

function wasRetried(config: InternalAxiosRequestConfig): boolean {
  return (config as InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean })[RETRY_FLAG] === true
}

/**
 * Registers auth route headers + single-flight 401 refresh (matches Flutter DioAuthInterceptor).
 * Call once when creating the HTTP client.
 */
export function registerWorkspaceAuthInterceptors(client: AxiosInstance): () => void {
  const ejectAuthRoutes = registerAuthRouteRequestInterceptor(client)

  const responseId = client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!(error instanceof AxiosError) || error.response?.status !== 401) {
        return Promise.reject(error)
      }
      const config = error.config
      // Never refresh-on-401 for login/refresh themselves — that deadlocks the in-flight refresh.
      if (!config || shouldSkipAuthRefreshForUrl(config.url)) {
        return Promise.reject(error)
      }
      if (wasRetried(config)) {
        handleUnrecoverableAuthFailure()
        return Promise.reject(error)
      }
      const ok = await coordinatedRefresh(client)
      if (!ok) {
        handleUnrecoverableAuthFailure()
        return Promise.reject(error)
      }
      return client.request(markRetried(config))
    },
  )

  return () => {
    ejectAuthRoutes()
    client.interceptors.response.eject(responseId)
  }
}
